'use client';

import { useCallback, useEffect, useState } from 'react';

const secondsUntil = (deadline: number | null) => (deadline === null ? 0 : Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));

/**
 * Counts a duration down to zero, one second at a time.
 *
 * The duration is turned into an absolute deadline because the backend sends the same number
 * every time (e.g. 30). Restarting has to be an explicit `start()` call rather than an effect
 * watching the value, since such an effect would not re-fire on 30 -> 30.
 */
export function useCountdown() {
  const [deadline, setDeadline] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  /** `undefined` = the response carried no cooldown, so keep any running countdown. */
  const start = useCallback((seconds?: number) => {
    if (typeof seconds !== 'number') {
      return;
    }
    const next = seconds > 0 ? Date.now() + seconds * 1000 : null;
    setDeadline(next);
    setSecondsLeft(secondsUntil(next));
  }, []);

  const reset = useCallback(() => {
    setDeadline(null);
    setSecondsLeft(0);
  }, []);

  useEffect(() => {
    if (deadline === null) {
      return;
    }
    // Recompute from the deadline instead of decrementing, so a throttled tab can't drift.
    const timer = setInterval(() => {
      const remaining = secondsUntil(deadline);
      setSecondsLeft(remaining);
      if (remaining === 0) {
        setDeadline(null); // stops the interval via cleanup
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  return { secondsLeft, start, reset };
}
